import { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';

import { AuthCard } from '../components/common/AuthCard';
import {
    FormAlert,
    PasswordField,
    SubmitButton,
    TextField,
    vEmail,
    vLoginPassword,
} from '../components/form';
import { failCountOf, get, post, statusOf } from '../lib/api';
import config from '../lib/config';
import { loginUser, useAuthDispatch } from '../auth/AuthContext';
import type { LocalData } from '../auth/types';
import { getLoginEndpoints, type AuthController } from '../auth/endpoints';
import { Paths } from '../routes/paths';
import { usePageTitle } from '../lib/usePageTitle';

const schema = z.object({
    userName: vEmail,
    password: vLoginPassword,
});
type LoginValues = z.infer<typeof schema>;

// reCAPTCHA appears once a user has more failed attempts than this.
const CAPTCHA_LIMIT = 5;

export function LoginPage({ controller }: { controller: AuthController }) {
    const { t } = useTranslation();
    usePageTitle(); // login uses the base title
    const dispatch = useAuthDispatch();
    const endpoints = getLoginEndpoints(controller);
    const isAd = controller === 'activeDirectory';
    const [searchParams] = useSearchParams();
    // Set by the 401 interceptor (api.ts) when an authenticated session lapsed.
    const sessionExpired = searchParams.get('expired') === '1';

    const methods = useForm<LoginValues>({
        resolver: zodResolver(schema),
        defaultValues: { userName: '', password: '' },
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const [error, setError] = useState('');
    const [failCount, setFailCount] = useState(0);
    const [captcha, setCaptcha] = useState('');
    const [done, setDone] = useState(false);
    const captchaRef = useRef<ReCAPTCHA | null>(null);

    useEffect(() => {
        get<number>('login/LoginErrorCount')
            .then((res) => setFailCount(res.data ?? 0))
            .catch(() => {
                /* count is best-effort; ignore */
            });
    }, []);

    const captchaRequired = failCount > CAPTCHA_LIMIT;
    const blockedByCaptcha = captchaRequired && captcha.length === 0;

    function resetCaptcha() {
        try {
            captchaRef.current?.reset();
        } catch {
            /* widget only mounts past the limit */
        }
        setCaptcha('');
    }

    async function onSubmit(values: LoginValues) {
        setError('');
        try {
            const res = await post<LocalData>(endpoints.credentials, {
                userName: values.userName,
                password: values.password,
                captchaKey: captcha,
            });
            if (res.status === 200 && res.data) {
                loginUser(dispatch, res.data);
                setDone(true);
            } else {
                resetCaptcha();
                setError(t('common.somethingWentWrong'));
            }
        } catch (err) {
            resetCaptcha();
            const fc = failCountOf(err);
            if (typeof fc === 'number') setFailCount(fc);
            const status = statusOf(err);
            if (status === 400 || status === 401) setError(t('login.error'));
            // 406: AD credentials without a full user@domain username.
            else if (status === 406) setError(t('validation.emailWithDomain'));
            else setError(t('common.somethingWentWrong'));
        }
    }

    if (done) {
        // 2FA (AuthOptions) always uses the login/* controller regardless of
        // how the credentials were checked — no state needs to travel along.
        return <Navigate to={Paths.authOptions} replace />;
    }

    return (
        <AuthCard title={t('login.title')} subtitle={isAd ? t('login.adSubtitle') : t('login.subtitle')}>
            <FormProvider {...methods}>
                {sessionExpired && !error && <FormAlert variant="info">{t('login.expired')}</FormAlert>}
                <FormAlert onClose={() => setError('')}>{error || undefined}</FormAlert>
                <Form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
                    <TextField
                        name="userName"
                        label={t('login.username')}
                        type="email"
                        placeholder={t('login.usernamePlaceholder')}
                        autoComplete="username"
                        autoFocus
                        required
                    />
                    <PasswordField
                        name="password"
                        label={t('login.password')}
                        placeholder={t('login.passwordPlaceholder')}
                        autoComplete="current-password"
                        required
                    />

                    {captchaRequired && (
                        <div className="mb-3 captcha-wrap">
                            <ReCAPTCHA
                                ref={captchaRef}
                                sitekey={config.recaptchaSiteKey}
                                onChange={(value) => setCaptcha(value ?? '')}
                            />
                            <Form.Text className="text-muted d-block mt-1">
                                {t('login.captchaHint')}
                            </Form.Text>
                        </div>
                    )}

                    <div className="d-flex align-items-center gap-3 mt-2">
                        <SubmitButton loading={methods.formState.isSubmitting} disabled={blockedByCaptcha}>
                            {t('login.submit')}
                        </SubmitButton>
                        {!isAd && (
                            <Link to={Paths.forgotPassword} className="small">
                                {t('login.forgotPassword')}
                            </Link>
                        )}
                    </div>
                </Form>
            </FormProvider>
        </AuthCard>
    );
}
